/** THE chart data path (SM-P3-11, ESC-008). One adapter, three modes, and nothing else may fetch a bar.
 *
 *  `<pf-tv-chart>` builds its TradingView datafeed from `window.PFMarketData`, which synthesises a client-side walk
 *  when it has no bars for a symbol. That was the whole chart data path: `/udf` existed, was session-gated and
 *  conformance-tested, and **nothing called it** — so the candles a trader read were invented in their own browser
 *  and had no relationship to the prices the order ticket was sized against.
 *
 *  This loads the stored series over `/udf/history` and hands it to `PFMarketData.register`, the package's own
 *  documented extension point for feeding real history in. Registration is what makes `isSynthetic` false, which is
 *  what turns the chart's own footnote from "reference series" into the real source.
 *
 *  Registering rather than replacing the datafeed is deliberate. The element owns the widget lifecycle, the
 *  subscription bookkeeping and the resolution mapping; reaching past it to install a bespoke datafeed would mean
 *  re-implementing all three and diverging from the package on the next update. The seam the package documents is
 *  the seam to use. `tools/chart-lint` fails the build if anything else takes a bar from the store, which is what
 *  makes "the only path" a property of the repository rather than of whoever last edited a screen.
 *
 *  ## The three modes, and what actually differs between them
 *
 *  A mode is not decoration; it decides **whether the series can still change under the trader**.
 *
 *   · `trade` and `review` are LIVE: the tail of the series, ending at the current clock, re-primed whenever the
 *     desk retargets. This is the desk and the day just traded — the chart is expected to move.
 *
 *   · `replay` is a CLOSED window. A replay is a trader walking back through something that already happened, and
 *     the pane must not gain a bar while they scrub: what a candle was at 09:42 cannot depend on when they opened
 *     the screen. So the series is fixed for the life of the mount and is never re-fetched.
 *
 *   · `backtest` is a closed window under a SIMULATED CLOCK. Bars later than the cursor are not served, because a
 *     backtest that draws today's candle beside a result computed over last month's data is two clocks on one pane.
 *     The cursor is `simulatedNow`, which the run supplies; today no run does — see the note on `advanceTo`.
 *
 *  ## There is no realtime channel, and that is the design's decision rather than an omission
 *
 *  `subscribeBars` exists here and only here, so the answer to "where could a tick enter a chart?" is one function.
 *  It admits none. The design package's own datafeed says it in as many words —
 *  `subscribeBars: () => {}, // historical study surface — no realtime channel by design` — and `/udf/config`
 *  reports `supports_group_request: false` with a note that live prices belong on the ticketed `prices:{symbol}`
 *  fabric channel rather than on the history endpoint, because two live paths would be two truths. Nothing publishes
 *  to that channel today: no market-data provider is bound (ESC-006). Folding a *simulated* quote into a series the
 *  server sourced would produce a bar of mixed provenance, which is the exact failure the admission test below
 *  exists to prevent — so the seam is built, empty, and says why.
 */

interface Bar {
  readonly time: number;
  readonly open: number;
  readonly high: number;
  readonly low: number;
  readonly close: number;
  readonly volume: number;
}

/** The design package's store, as this module uses it. One declaration for both sources — the server path registers,
 *  the fixture path reads its own series back and re-registers it shifted. */
interface MarketDataStore {
  readonly resolutions?: readonly string[];
  bars(sym: string, res: string): readonly Bar[];
  register(sym: string, res: string, bars: readonly Bar[]): number;
  isSynthetic(sym: string, res: string): boolean;
  has?(sym: string, res: string): boolean;
}

interface UdfHistory {
  s: 'ok' | 'no_data' | 'error';
  t?: number[];
  o?: number[];
  h?: number[];
  l?: number[];
  c?: number[];
  v?: number[];
  nextTime?: number;
  errmsg?: string;
}

/** Where `/udf` lives.
 *
 *  Empty in the deployed topology, where the web edge proxies `/udf` to the private API and a relative path is
 *  both correct and the reason the session cookie is first-party. In development the two are separate origins, so
 *  the request has to name the API the same way the tRPC client does — and carry credentials, because `/udf` is
 *  session-gated like every other surface. Getting this wrong is quiet: a relative fetch against the dev server
 *  returns the SPA's own `index.html` with a 200, which parses as neither JSON nor bars. */
const BINDING = (import.meta.env['VITE_API_BINDING'] as string | undefined) ?? 'sim';

const API_ORIGIN =
  BINDING === 'http'
    ? ((import.meta.env['VITE_API_ORIGIN'] as string | undefined) ?? 'http://localhost:3000')
    : '';

/** Whether a chart is allowed to draw bars the server did not send.
 *
 *  Against a real server: no. The package synthesises a deterministic walk for any series nobody registered, and a
 *  synthetic candle rendered next to a real order ticket is a number a trader can act on. It is labelled — the
 *  element's own footnote says "reference series" — but a label is not a control, and the launch plan's rule is
 *  that no tester path may source generated bars at all. So in server mode an unregistered series is served as
 *  *no data*, and an empty pane is the honest answer.
 *
 *  Against the in-process sim: yes, and deliberately. That is the local demo fixture path, where the whole dataset
 *  is seeded and labelled and there is no server to disagree with. */
export const SERVER_HISTORY_REQUIRED = BINDING === 'http';

/** How much history to ask for, per resolution. Enough to fill the pane and scroll back through it without asking
 *  for years of bars nobody scrolls to. */
const BARS_WANTED = 700;

const STEP_SECONDS: Record<string, number> = {
  '1': 60,
  '5': 300,
  '15': 900,
  '30': 1_800,
  '60': 3_600,
  '240': 14_400,
  '1D': 86_400,
  '1W': 604_800,
};

/** The chart modes the element carries (`design-reference/pf-tv-chart.js` line 16). `trade` is its default, and is
 *  the default here for the same reason: an element with no mode attribute is the desk. */
export type ChartMode = 'trade' | 'review' | 'replay' | 'backtest';

/** Whether a mode's series may still change under the trader. See the header. */
export function isLiveMode(mode: ChartMode): boolean {
  return mode === 'trade' || mode === 'review';
}

export function modeOf(value: string | null | undefined): ChartMode {
  return value === 'replay' || value === 'backtest' || value === 'review' ? value : 'trade';
}

/** Series already fetched this session, so switching back to a symbol does not re-ask. Keyed the way the store is. */
const loaded = new Set<string>();

/** The simulated clock, per series, for `backtest`. Absent means "no cursor" — see `advanceTo`. */
const cursors = new Map<string, number>();

async function fetchWindow(symbol: string, resolution: string, to: number): Promise<UdfHistory> {
  const step = STEP_SECONDS[resolution] ?? 3_600;
  const from = to - step * BARS_WANTED;
  const query = new URLSearchParams({
    symbol,
    resolution,
    from: String(from),
    to: String(to),
  });
  const response = await fetch(`${API_ORIGIN}/udf/history?${query.toString()}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`udf/history ${response.status}`);
  return (await response.json()) as UdfHistory;
}

/** Loads one symbol/resolution into the store, and reports whether it succeeded.
 *
 *  The return value is the point. It used to be advisory — the caller fired this and retargeted the widget in the
 *  same pass, so the element asked its datafeed for the new series before the fetch resolved and got the synthetic
 *  walk. Now the caller waits for it and `hasServerBars` is what the datafeed checks, so a false here means the
 *  chart shows nothing rather than showing something invented. */
export async function primeServerBars(
  symbol: string,
  resolution: string,
  mode: ChartMode = 'trade',
  now: number = Date.now(),
): Promise<boolean> {
  const store = (globalThis as { PFMarketData?: MarketDataStore }).PFMarketData;
  if (!store || !symbol || !resolution) return false;
  const key = `${symbol} ${resolution}`;
  // A closed window is fetched once and never again. Re-fetching would be the bug the mode exists to prevent: the
  // pane gaining a bar under a trader who is walking back through a day that has already happened.
  if (loaded.has(key)) return true;

  try {
    const to = Math.floor(now / 1000);
    let history = await fetchWindow(symbol, resolution, to);

    // `no_data` with a `nextTime` is the feed saying "not here, but there" — the stored history ends before the
    // clock, which is the normal case for a seeded demo ledger. Asking once more at the time it names is the whole
    // reason the endpoint returns it; without this the chart would be blank on every instrument.
    if (history.s === 'no_data' && typeof history.nextTime === 'number')
      history = await fetchWindow(symbol, resolution, history.nextTime);

    if (history.s !== 'ok' || !history.t?.length) return false;

    const { t, o = [], h = [], l = [], c = [], v = [] } = history;
    const bars: Bar[] = t.map((seconds, i) => ({
      // The store keeps milliseconds; UDF speaks seconds. Getting this wrong puts every bar in 1970.
      time: seconds * 1000,
      open: o[i] ?? 0,
      high: h[i] ?? 0,
      low: l[i] ?? 0,
      close: c[i] ?? 0,
      volume: v[i] ?? 0,
    }));
    store.register(symbol, resolution, bars);
    loaded.add(key);
    // A closed window's cursor starts at its last bar, so a `backtest` pane serves the whole window until a run
    // moves it back. A live tail carries no cursor at all — `barsUpTo` is not consulted for it.
    if (!isLiveMode(mode)) cursors.set(key, bars[bars.length - 1]?.time ?? 0);
    return true;
  } catch {
    // Offline, unauthenticated, or the endpoint is down. In server mode the datafeed refuses the series rather than
    // letting the package invent one, so this resolves false and the chart is empty and says so.
    return false;
  }
}

/** Whether this series came from the server in this session.
 *
 *  The datafeed's admission test in server mode. Deliberately reads this module's own record of what it registered
 *  rather than `PFMarketData.isSynthetic`, because the store cannot tell a series *we* registered from one some
 *  other code path registered — and "the server sent it" is the property that matters. */
export function hasServerBars(symbol: string, resolution: string): boolean {
  return loaded.has(`${symbol} ${resolution}`);
}

/** Forgets what has been loaded. For tests, and for a sign-out that should not leave one tenant's bars in memory. */
export function resetServerBars(): void {
  loaded.clear();
  cursors.clear();
  subscriptions.clear();
}

/** Moves a `backtest` pane's simulated clock. Bars after it are not served.
 *
 *  NOTHING CALLS THIS YET, and that is a scope fact rather than an oversight. The product's Backtest runs
 *  `solveBacktest` in the model over sessions that carry no timestamps — `btMarkers` is `'[]'` for exactly that
 *  reason — so there is no simulated instant to advance to. Driving the cursor from anything else available (the
 *  wall clock, a fraction of the run's progress) would be inventing the one number the mode exists to be honest
 *  about. The mechanism is here, tested, and waits for a backtest that runs over dated bars (ESC-009). */
export function advanceTo(symbol: string, resolution: string, simulatedNow: number): void {
  cursors.set(`${symbol} ${resolution}`, simulatedNow);
}

/** The bars this pane may show: everything the server sent, cut at the simulated clock if the mode has one.
 *
 *  Returns `null` when the series was never primed, which the datafeed reads as "refuse" rather than "empty" — a
 *  series nobody fetched and a series with no bars in the window are different facts. */
export function barsUpTo(
  symbol: string,
  resolution: string,
  mode: ChartMode,
  bars: readonly { time: number }[],
): readonly { time: number }[] | null {
  const key = `${symbol} ${resolution}`;
  if (!loaded.has(key)) return null;
  if (mode !== 'backtest') return bars;
  const cursor = cursors.get(key);
  return cursor === undefined ? bars : bars.filter((bar) => bar.time <= cursor);
}

/* ── the subscription seam ─────────────────────────────────────────────────────────────────────────────────────
 *
 *  One place a tick could enter a chart, so there is one place to look. It admits none — see the header. The
 *  bookkeeping is real (a subscriber is recorded and can be removed) because a seam that dropped its callers on the
 *  floor would leak them the day something does publish, and because `unsubscribeBars` has to be able to answer
 *  truthfully whether it removed anything. */

interface Subscription {
  readonly symbol: string;
  readonly resolution: string;
  readonly mode: ChartMode;
  readonly onBar: (bar: Bar) => void;
  stop: () => void;
  /** The bar under construction, updated tick by tick until its boundary passes. */
  developing: Bar | null;
}

const subscriptions = new Map<string, Subscription>();

/** The tick source, injectable so the suite can drive ticks without a socket. Production wires `onPrice`. */
type TickSource = (symbol: string, handler: (tick: { last: number; at: number }) => void) => () => void;
let tickSource: TickSource | null = null;
export function setTickSource(source: TickSource | null): void {
  tickSource = source;
}

/** Fold one tick into a subscription's developing bar and hand the result to the widget.
 *
 *  The classic shape: a tick inside the current bar's window updates high/low/close; a tick past the boundary
 *  closes that bar and opens the next at the tick's price. The FIRST tick after subscribe seeds the developing bar
 *  from the last HISTORICAL bar when the tick still falls inside its window — so the bar a trader watched grow out
 *  of history keeps growing rather than restarting from a lone tick. Volume stays 0: the price stream carries no
 *  volume, and inventing one is exactly what this file exists to refuse.
 */
function foldTick(sub: Subscription, tick: { last: number; at: number }): void {
  const stepMs = (STEP_SECONDS[sub.resolution] ?? 3_600) * 1000;
  const boundary = Math.floor(tick.at / stepMs) * stepMs;

  if (sub.developing === null) {
    const history = barsFor(sub.symbol, sub.resolution);
    const lastKnown = history[history.length - 1];
    sub.developing =
      lastKnown && Math.floor(lastKnown.time / stepMs) * stepMs === boundary
        ? { ...lastKnown }
        : { time: boundary, open: tick.last, high: tick.last, low: tick.last, close: tick.last, volume: 0 };
  }

  if (boundary > sub.developing.time) {
    // The old bar is complete as last delivered; the new one opens at this tick.
    sub.developing = {
      time: boundary,
      open: tick.last,
      high: tick.last,
      low: tick.last,
      close: tick.last,
      volume: 0,
    };
  } else {
    sub.developing = {
      ...sub.developing,
      high: Math.max(sub.developing.high, tick.last),
      low: Math.min(sub.developing.low, tick.last),
      close: tick.last,
    };
  }
  sub.onBar(sub.developing);
}

/** Registers interest in new bars for a series. Returns whether ticks can actually arrive.
 *
 *  Since SM-P6-04 this is a real subscription under the http binding: ticks ride the ticketed `prices:{symbol}`
 *  fabric channel from the deployment's one provider connection, and each tick folds into the developing bar
 *  above. `false` still means what it always meant — this pane will never tick — which remains the truthful
 *  answer for a closed window, for the sim binding (no fabric, deliberately no fake one), and for a symbol the
 *  provider map does not carry (surfaced on /health, never substituted).
 */
export function subscribeBars(
  uid: string,
  symbol: string,
  resolution: string,
  mode: ChartMode,
  onBar: (bar: Bar) => void,
): boolean {
  // A closed window refuses outright. `replay` and `backtest` are records of something that already happened, and a
  // tick arriving on one would change a bar the trader is in the middle of reading.
  if (!isLiveMode(mode)) return false;

  const source = tickSource;
  if (!source) {
    // No tick source wired (sim binding, or a test that has not injected one): subscribed and quiet, honestly.
    subscriptions.set(uid, {
      symbol,
      resolution,
      mode,
      onBar,
      stop: () => undefined,
      developing: null,
    });
    return false;
  }

  // One object, closed over by the tick handler and held by the map — `foldTick` mutates `developing` in place,
  // so a copy in either place would be a bar nobody updates.
  const sub = { symbol, resolution, mode, onBar, stop: () => undefined, developing: null } as Subscription & {
    stop: () => void;
  };
  sub.stop = source(symbol, (tick) => foldTick(sub, tick));
  subscriptions.set(uid, sub);
  return true;
}

export function unsubscribeBars(uid: string): boolean {
  const sub = subscriptions.get(uid);
  sub?.stop();
  return subscriptions.delete(uid);
}

/** How many subscribers a series is holding. For the suite, and for the dev console. */
export function subscriberCount(): number {
  return subscriptions.size;
}

/* ── the demo fixture path (sim binding only) ──────────────────────────────────────────────────────────────────
 *
 *  Merged in from `screens/market-data.ts`, so that "where does this chart's bars come from?" has exactly one file
 *  as its answer and `tools/chart-lint` can say so without an exception list. There are two sources and they are
 *  mutually exclusive by binding: the server, above, and this — the design package's own seeded series, anchored to
 *  the present, used when there is no server to disagree with.
 *
 *  `PFMarketData` synthesises its bars ending at a **fixed** instant — `Date.UTC(2026, 6, 24, 14, 0, 0)`, the day
 *  the design package was authored. TradingView opens a chart at the current clock and asks the datafeed for bars
 *  there; the package's `getBars` answers `{ noData: true }` with no `nextTime`, which is the library's signal for
 *  *there is nothing at all*, so it stops asking and draws an empty pane. On 2026-07-24 the charts worked. Every day
 *  after, they are blank — a time-bomb, not a porting error, and it fires in the design's own reference render too.
 *
 *  The fix is the package's own documented extension point: `register(symbol, resolution, bars)`. Here it
 *  re-registers the store's **own** deterministic series shifted forward so the last bar lands on the current hour.
 *
 *  Shifted rather than extended: extending would invent a month of prices that never existed, where shifting moves
 *  the same synthesised shape — the same bars, the same seeded walk — to where the chart is looking. The series
 *  carries no claim about any real instrument on any real date; it is labelled simulated wherever it is shown, and
 *  its calendar position was always arbitrary. Under the `http` binding none of this runs: `SERVER_HISTORY_REQUIRED`
 *  refuses any series the server did not send, so an anchored fixture would be registered and then never served.
 */

/** The instruments the app charts, at the price the desk quotes them.
 *
 *  `PFMarketData` carries its own base per symbol and the desk carries the demo's quote table, and for most symbols
 *  the two disagree — EURUSD is 1.0864 in the store and 1.1376 on the desk. Left alone, the trader reads 1.1376 in
 *  the header while the chart draws 1.08, which is the chart and the ticket describing different worlds. The series
 *  is scaled so its last bar lands on the quoted price; the shape is untouched.
 *
 *  `null` means "leave the store's own base alone" — XAUUSD, where the two already agree to within a tenth of a
 *  point and rescaling would move a series the entire reference was drawn against. */
const CHARTED: readonly (readonly [string, number | null])[] = [
  ['XAUUSD', null],
  ['EURUSD', 1.13761],
  ['GBPJPY', 198.42],
  ['US30', 44120.5],
  ['USDJPY', 163.787],
  ['NAS100', 20343.3],
  ['SPX500', null],
  ['BTCUSD', 96226.2],
];
const RESOLUTIONS = ['15', '60'] as const;

const STEP_MS: Record<string, number> = {
  '1': 60_000,
  '5': 300_000,
  '15': 900_000,
  '30': 1_800_000,
  '60': 3_600_000,
  '240': 14_400_000,
  '1D': 86_400_000,
};

/** Re-registers each charted series ending at `now`. Idempotent per symbol/resolution. */
export function anchorMarketData(now: number = Date.now()): void {
  const md = (globalThis as { PFMarketData?: MarketDataStore }).PFMarketData;
  if (!md) return;

  for (const [symbol, quoted] of CHARTED) {
    for (const res of RESOLUTIONS) {
      // Already anchored (or fed with real history) — leave it alone rather than shifting twice.
      if (!md.isSynthetic(symbol, res)) continue;
      const bars = md.bars(symbol, res);
      const last = bars[bars.length - 1];
      if (!last) continue;

      const step = STEP_MS[res] ?? STEP_MS['60']!;
      // Land on a clean boundary so the final candle is a whole bar rather than a partial one the widget would draw
      // short.
      const target = Math.floor(now / step) * step;
      const shift = target - last.time;
      // Multiplicative, so the walk keeps its proportions: a 0.4% candle stays a 0.4% candle at the new price.
      const factor = quoted && last.close > 0 ? quoted / last.close : 1;
      if (shift <= 0 && factor === 1) continue;

      md.register(
        symbol,
        res,
        bars.map((b: Bar) => ({
          time: b.time + Math.max(0, shift),
          open: b.open * factor,
          high: b.high * factor,
          low: b.low * factor,
          close: b.close * factor,
          volume: b.volume,
        })),
      );
    }
  }
}

/** The bars the adapter holds for a series, or an empty list.
 *
 *  The one read anything outside this module gets, and it is deliberately narrow: callers want to *position*
 *  something against the series the chart is showing (a trade marker, a `nextTime` hint), not to source data from
 *  it. Handing back the store's own array keeps that a read.
 */
export function barsFor(symbol: string, resolution: string): readonly Bar[] {
  const store = (globalThis as { PFMarketData?: { bars(s: string, r: string): readonly Bar[] } })
    .PFMarketData;
  if (!store || !symbol || !resolution) return [];
  return store.bars(symbol, resolution) ?? [];
}

/** The open of the most recent bar at or before `fromMs`, in UDF seconds, or null.
 *
 *  TradingView's `nextTime`: a bare `noData` means *there is nothing at all for this symbol*, so the library stops
 *  asking and draws an empty pane. Stored history ends when the ledger ends, which for a seeded demo is not today,
 *  so every chart was blank on first paint until this told the library where the series actually is.
 */
export function nextTimeBefore(symbol: string, resolution: string, fromMs: number): number | null {
  let latest: number | null = null;
  for (const bar of barsFor(symbol, resolution))
    if (bar.time <= fromMs && (latest === null || bar.time > latest)) latest = bar.time;
  return latest === null ? null : Math.floor(latest / 1000);
}
